<template>
  <div
    v-show="currentTourPage === pageNumber && shouldShowInteractiveTour"
    ref="tourWindowElement"
    class="interactive-tour-window"
    :style="windowPositionAdjustments"
  >
    <div class="pointer-triangle" :style="trianglePositionStyle"></div>
    <div class="tour-page-header">
      <slot name="main-header"></slot>
    </div>
    <div class="tour-page-explanation">
      <slot name="explanation"></slot>
    </div>
    <div>
      <button
        type="button"
        class="sc3-button sc3-button-grey tour-button"
        :disabled="currentTourPageIndex === 0"
        @click="previousTourPage()"
      >Back</button>
      <button
        type="button"
        class="sc3-button sc3-button-green tour-button"
        @click="nextTourPage()"
      >Next</button>
    </div>
    <div>
      <button
        type="button"
        class="sc3-button-link end-tour-button"
        @click="endInteractiveTour()"
      >End Tour</button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { CSSProperties, computed, onMounted, ref, toRefs, watch, nextTick } from 'vue';
  import { useInteractiveTour, InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import { useScreenSizing } from '@/composables/useScreenSizing';

  const { shouldShowInteractiveTour } = useScreenSizing();

  interface PositionStyle {
    top?: ((dimensions: TriangleDimensions) => string) | string;
    right?: ((dimensions: TriangleDimensions) => string) | string;
    bottom?: ((dimensions: TriangleDimensions) => string) | string;
    left?: ((dimensions: TriangleDimensions) => string) | string;
    transform?: ((dimensions: TriangleDimensions) => string) | string;
  }

  interface TriangleDimensions {
    altitude: number;
    baseWidth: number;
  }

  const props = defineProps<{
    pageNumber: number;
    arrowPosition: InteractiveTourTrianglePosition;
    position?: PositionStyle;
    isWide?: boolean;
  }>();

  const emit = defineEmits<{
    (event: 'visible', pageNumber: number): void;
  }>();

  const tourWindowElement = ref<HTMLDivElement>();
  const { pageNumber, arrowPosition: triangleRelativePosition, isWide } = toRefs(props);
  const { currentTourPage, currentTourPageIndex, registerTourPage, nextTourPage, previousTourPage, endInteractiveTour } = useInteractiveTour();

  const TRIANGLE_DIMENSIONS: TriangleDimensions = {
    altitude: 10,
    baseWidth: 8
  };

  const FAR_PERCENTAGE_POSITION = `calc(100% - ${TRIANGLE_DIMENSIONS.baseWidth}px)`;
  const CENTER_PERCENTAGE_POSITION = '50%';
  const CLOSE_PERCENTAGE_POSITION = `${TRIANGLE_DIMENSIONS.baseWidth}px`;

  const trianglePositionStyle = computed(() => {
    switch (triangleRelativePosition.value) {
      case InteractiveTourTrianglePosition.TopLeft:
        return getTopPositioning(FAR_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.TopCenter:
        return getTopPositioning(CENTER_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.TopRight:
        return getTopPositioning(CLOSE_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.LeftTop:
        return getLeftPositioning(CLOSE_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.LeftCenter:
        return getLeftPositioning(CENTER_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.LeftBottom:
        return getLeftPositioning(FAR_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.BottomLeft:
        return getBottomPositioning(FAR_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.BottomCenter:
        return getBottomPositioning(CENTER_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.BottomRight:
        return getBottomPositioning(CLOSE_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.RightTop:
        return getRightPositioning(CLOSE_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.RightCenter:
        return getRightPositioning(CENTER_PERCENTAGE_POSITION);
      case InteractiveTourTrianglePosition.RightBottom:
        return getRightPositioning(FAR_PERCENTAGE_POSITION);
    }
  });

  const windowBorderRounding = computed(() => {
    switch (triangleRelativePosition.value) {
      case InteractiveTourTrianglePosition.TopLeft:
      case InteractiveTourTrianglePosition.LeftTop:
        return `0px 5px 5px 5px`;
      case InteractiveTourTrianglePosition.TopRight:
      case InteractiveTourTrianglePosition.RightTop:
        return `5px 0px 5px 5px`;
      case InteractiveTourTrianglePosition.RightBottom:
      case InteractiveTourTrianglePosition.BottomRight:
        return `5px 5px 0px 5px`;
      case InteractiveTourTrianglePosition.BottomLeft:
      case InteractiveTourTrianglePosition.LeftBottom:
        return `5px 5px 5px 0px`;
      default:
        return `5px`;
    }
  });

  const defaultWindowAbsolutePositionAdjustmentStyle = computed<CSSProperties>(() => {
    const POSITION_ADJUSTMENT = `${TRIANGLE_DIMENSIONS.altitude}px`;
    switch (triangleRelativePosition.value) {
      case InteractiveTourTrianglePosition.TopLeft:
      case InteractiveTourTrianglePosition.TopCenter:
      case InteractiveTourTrianglePosition.TopRight:
        return { top: `calc(100% + ${POSITION_ADJUSTMENT})` };
      case InteractiveTourTrianglePosition.RightTop:
      case InteractiveTourTrianglePosition.RightCenter:
      case InteractiveTourTrianglePosition.RightBottom:
        return { right: POSITION_ADJUSTMENT };
      case InteractiveTourTrianglePosition.BottomRight:
      case InteractiveTourTrianglePosition.BottomCenter:
      case InteractiveTourTrianglePosition.BottomLeft:
        return { bottom: `calc(100% + ${POSITION_ADJUSTMENT})` };
      case InteractiveTourTrianglePosition.LeftBottom:
      case InteractiveTourTrianglePosition.LeftCenter:
      case InteractiveTourTrianglePosition.LeftTop:
        return { left: POSITION_ADJUSTMENT };
    }
  });

  const windowPositionAdjustments = computed<CSSProperties>(() => {
    const positionObject: Record<string, string> = {};
    const top = getPositionValue(props.position?.top);
    const right = getPositionValue(props.position?.right);
    const bottom = getPositionValue(props.position?.bottom);
    const left = getPositionValue(props.position?.left);
    const transform = getPositionValue(props.position?.transform);
    if (top)
      positionObject.top = top;
    if (right)
      positionObject.right = right;
    if (bottom)
      positionObject.bottom = bottom;
    if (left)
      positionObject.left = left;
    if (transform)
      positionObject.transform = transform;

    return {
      ...defaultWindowAbsolutePositionAdjustmentStyle.value,
      ...positionObject
    }
  });

  function getPositionValue(positionValue?: ((dimensions: TriangleDimensions) => string) | string): string | undefined {
    if (!positionValue)
      return undefined;

    if (typeof positionValue === 'string')
      return positionValue;

    return positionValue(TRIANGLE_DIMENSIONS);
  }

  function getTopPositioning(right: string): CSSProperties {
    return {
      top: `-${TRIANGLE_DIMENSIONS.altitude / 2}px`,
      right,
    };
  };

  function getRightPositioning(top: string): CSSProperties {
    return {
      top,
      right: `-${TRIANGLE_DIMENSIONS.altitude / 2}px`,
      rotate: '90deg'
    };
  };

  function getBottomPositioning(right: string): CSSProperties {
    return {
      top: `calc(100% + ${TRIANGLE_DIMENSIONS.altitude / 2}px)`,
      right: right,
      rotate: '180deg'
    };
  };

  function getLeftPositioning(top: string): CSSProperties {
    return {
      top,
      right: `calc(100% + ${TRIANGLE_DIMENSIONS.altitude / 2}px)`,
      rotate: '-90deg'
    };
  };

  function handleScrollToTourWindow() {
    const tourWindowBoundingClient = tourWindowElement.value?.getBoundingClientRect();
    if (!tourWindowBoundingClient)
      return;

    const { top: tourWindowTop, height: tourWindowHeight } = tourWindowBoundingClient;
    const topOfTourWindowVerticalPosition = window.scrollY + tourWindowTop;
    const centeredTourWindowScrollPosition = topOfTourWindowVerticalPosition - (window.innerHeight / 2) + (tourWindowHeight / 2);

    window.scrollTo({
      top: Math.max(0, centeredTourWindowScrollPosition),
      behavior: 'smooth'
    });
  }

  onMounted(() => {
    registerTourPage(pageNumber.value);
  });

  watch(currentTourPage, newPageNumber => {
    if (newPageNumber === pageNumber.value) {
      emit('visible', pageNumber.value);
    }
  });

  watch(currentTourPage, async newPageNumber => {
    if (newPageNumber === pageNumber.value) {
      await nextTick();
      handleScrollToTourWindow();
    }
  });
</script>

<style scoped>
  .interactive-tour-window {
    position: absolute;
    text-align: center;
    z-index: 9999;
    width: v-bind('!!isWide ? "450px" : "300px"');
    background-color: #ffffff;
    color: #000000;
    padding: 6px 12px;
    border-radius: v-bind('windowBorderRounding');
    white-space: normal;
  }

  .pointer-triangle {
    position: absolute;
    translate: 50% -50%;
    width: 0;
    height: 0;
    border-left: v-bind('`${TRIANGLE_DIMENSIONS.baseWidth}px`') solid transparent;
    border-right: v-bind('`${TRIANGLE_DIMENSIONS.baseWidth}px`') solid transparent;
    border-bottom: v-bind('`${TRIANGLE_DIMENSIONS.altitude}px`') solid #ffffff;
  }

  .tour-page-header {
    text-align: center;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 18px;
    font-weight: 900;
  }

  .tour-page-explanation {
    font-size: 13px;
    color: #707f8f;
  }

  .tour-button {
    padding: 4px 10px;
  }

  .end-tour-button {
    font-size: 11px;
  }
</style>

<style>
  .interactive-tour-container {
    position: relative;
  }
</style>
